/**
 * DateRangeFilter Component
 * 
 * A reusable date range filter with preset options and custom date selection.
 * Uses YYYY-MM format for month-based filtering.
 */

import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, ChevronDown, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

export interface DateRange {
  startDate?: string; // YYYY-MM format
  endDate?: string;   // YYYY-MM format
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  minDate?: string; // YYYY-MM format
  maxDate?: string; // YYYY-MM format
  className?: string;
}

type PresetKey = 'all' | 'last3months' | 'last6months' | 'last12months' | 'thisYear' | 'lastYear' | 'custom';

interface Preset {
  key: PresetKey;
  label: string;
  getRange: (minDate?: string, maxDate?: string) => DateRange;
}

// Helper to format YYYY-MM to display format
function formatMonthDisplay(month: string): string {
  if (!month) return '';
  const monthStr = String(month);
  const [year, m] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Helper to get YYYY-MM for n months ago
function getMonthsAgo(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Helper to get current month
function getCurrentMonth(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const presets: Preset[] = [
  {
    key: 'all',
    label: 'All Time',
    getRange: () => ({}),
  },
  {
    key: 'last3months',
    label: 'Last 3 Months',
    getRange: () => ({
      startDate: getMonthsAgo(3),
      endDate: getCurrentMonth(),
    }),
  },
  {
    key: 'last6months',
    label: 'Last 6 Months',
    getRange: () => ({
      startDate: getMonthsAgo(6),
      endDate: getCurrentMonth(),
    }),
  },
  {
    key: 'last12months',
    label: 'Last 12 Months',
    getRange: () => ({
      startDate: getMonthsAgo(12),
      endDate: getCurrentMonth(),
    }),
  },
  {
    key: 'thisYear',
    label: 'This Year',
    getRange: () => ({
      startDate: `${new Date().getFullYear()}-01`,
      endDate: getCurrentMonth(),
    }),
  },
  {
    key: 'lastYear',
    label: 'Last Year',
    getRange: () => {
      const lastYear = new Date().getFullYear() - 1;
      return {
        startDate: `${lastYear}-01`,
        endDate: `${lastYear}-12`,
      };
    },
  },
];

export default function DateRangeFilter({
  value,
  onChange,
  minDate,
  maxDate,
  className = '',
}: DateRangeFilterProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value.startDate || '');
  const [customEnd, setCustomEnd] = useState(value.endDate || '');

  // Determine active preset
  const activePreset = useMemo((): PresetKey => {
    if (!value.startDate && !value.endDate) return 'all';
    
    for (const preset of presets) {
      if (preset.key === 'all') continue;
      const range = preset.getRange(minDate, maxDate);
      if (range.startDate === value.startDate && range.endDate === value.endDate) {
        return preset.key;
      }
    }
    return 'custom';
  }, [value, minDate, maxDate]);

  // Generate month options for custom selector
  const monthOptions = useMemo(() => {
    const options: string[] = [];
    const start = String(minDate || '2023-01');
    const end = String(maxDate || getCurrentMonth());
    
    const [startYear, startMonth] = start.split('-').map(Number);
    const [endYear, endMonth] = end.split('-').map(Number);
    
    let current = new Date(startYear, startMonth - 1);
    const endDate = new Date(endYear, endMonth - 1);
    
    while (current <= endDate) {
      options.push(`${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`);
      current.setMonth(current.getMonth() + 1);
    }
    
    return options;
  }, [minDate, maxDate]);

  // Update custom fields when value changes externally
  useEffect(() => {
    setCustomStart(value.startDate || '');
    setCustomEnd(value.endDate || '');
  }, [value]);

  const handlePresetSelect = (preset: Preset) => {
    const range = preset.getRange(minDate, maxDate);
    onChange(range);
  };

  const handleCustomApply = () => {
    onChange({
      startDate: customStart || undefined,
      endDate: customEnd || undefined,
    });
    setIsCustomOpen(false);
  };

  const handleClear = () => {
    onChange({});
    setCustomStart('');
    setCustomEnd('');
  };

  const getDisplayLabel = (): string => {
    if (activePreset === 'all') return 'All Time';
    if (activePreset === 'custom') {
      if (value.startDate && value.endDate) {
        return `${formatMonthDisplay(value.startDate)} - ${formatMonthDisplay(value.endDate)}`;
      }
      if (value.startDate) return `From ${formatMonthDisplay(value.startDate)}`;
      if (value.endDate) return `Until ${formatMonthDisplay(value.endDate)}`;
      return 'Custom';
    }
    return presets.find(p => p.key === activePreset)?.label || 'Select Range';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            <span>{getDisplayLabel()}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {presets.map((preset) => (
            <DropdownMenuItem
              key={preset.key}
              onClick={() => handlePresetSelect(preset)}
              className={activePreset === preset.key ? 'bg-accent' : ''}
            >
              {preset.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
            <PopoverTrigger asChild>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsCustomOpen(true);
                }}
                className={activePreset === 'custom' ? 'bg-accent' : ''}
              >
                Custom Range...
              </DropdownMenuItem>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="start" side="right">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="start-month">Start Month</Label>
                  <select
                    id="start-month"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">No start date</option>
                    {monthOptions.map((month) => (
                      <option key={month} value={month}>
                        {formatMonthDisplay(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-month">End Month</Label>
                  <select
                    id="end-month"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">No end date</option>
                    {monthOptions.map((month) => (
                      <option key={month} value={month}>
                        {formatMonthDisplay(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setIsCustomOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={handleCustomApply}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </DropdownMenuContent>
      </DropdownMenu>

      {(value.startDate || value.endDate) && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleClear}
          title="Clear filter"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
